/**
 * Currency Role - Validate Pillar
 *
 * Validação semântica de valores monetários.
 * Zero dependências externas - pode ser usado standalone.
 *
 * Currency valida o VALOR NUMÉRICO, não o tipo de moeda.
 * O código da moeda é metadata, usado apenas para formatação.
 *
 * @example
 * import { validateCurrency, isValidCurrency } from '@attrx/role-morphic/currency/validate';
 *
 * validateCurrency(100.50);              // { valid: true, errors: [] }
 * validateCurrency(-50);                 // { valid: false, errors: ['Currency value cannot be negative'] }
 * validateCurrency(100.999, { decimals: 2 }); // { valid: false, errors: ['...too many decimal places'] }
 * isValidCurrency(100.50);               // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface CurrencyValidationConfig {
  /** Valor mínimo permitido (default: 0) */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Máximo de casas decimais (default: sem limite) */
  decimals?: number;
  /** Permite valores negativos? (default: false) */
  allowNegative?: boolean;
  /** Mensagem de erro para min */
  minError?: string;
  /** Mensagem de erro para max */
  maxError?: string;
}

// =============================================================================
// CONFIG
// =============================================================================

/**
 * Configuração padrão de validação para currency
 */
export const CURRENCY_VALIDATION_CONFIG: CurrencyValidationConfig = {
  min: 0,
  allowNegative: false,
  minError: "Currency value cannot be negative",
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor monetário
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateCurrency(100.50);                      // { valid: true, errors: [] }
 * validateCurrency(-50);                         // { valid: false, errors: ['Currency value cannot be negative'] }
 * validateCurrency(100.999, { decimals: 2 });    // { valid: false, errors: ['Value has too many decimal places (max: 2)'] }
 * validateCurrency(1000000, { max: 10000 });     // { valid: false, errors: ['Value must be at most 10000'] }
 * validateCurrency(-50, { allowNegative: true }); // { valid: true, errors: [] }
 */
export function validateCurrency(
  value: unknown,
  config: CurrencyValidationConfig = CURRENCY_VALIDATION_CONFIG
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Negative check (se não permitido)
  if (!config.allowNegative && value < 0) {
    const msg = config.minError ?? "Currency value cannot be negative";
    errors.push(msg);
  }

  // Min check (se allowNegative, pode ter min customizado)
  if (config.allowNegative && config.min !== undefined && value < config.min) {
    const msg = config.minError ?? `Value must be at least ${config.min}`;
    errors.push(msg);
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  // Decimals check
  if (config.decimals !== undefined) {
    const decimalPlaces = countDecimalPlaces(value);
    if (decimalPlaces > config.decimals) {
      errors.push(
        `Value has too many decimal places (max: ${config.decimals})`
      );
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor monetário é válido
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidCurrency(100.50);   // true
 * isValidCurrency(-50);      // false
 */
export function isValidCurrency(
  value: unknown,
  config: CurrencyValidationConfig = CURRENCY_VALIDATION_CONFIG
): boolean {
  return validateCurrency(value, config).valid;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Conta casas decimais de um número
 */
function countDecimalPlaces(value: number): number {
  if (Number.isInteger(value)) {
    return 0;
  }

  const str = String(value);
  const decimalIndex = str.indexOf(".");

  if (decimalIndex === -1) {
    return 0;
  }

  return str.length - decimalIndex - 1;
}
